import { supabase } from "@/integrations/supabase/client";

export interface SeoConfig {
  title: string;
  description: string;
  canonicalUrl?: string;
  robots: {
    index: boolean;
    follow: boolean;
  };
  openGraph: {
    title: string;
    description: string;
    image?: string;
    url?: string;
    type: string;
    siteName: string;
  };
  twitter: {
    card: string;
    title: string;
    description: string;
    image?: string;
  };
  jsonLd: Record<string, unknown>[];
}

export interface SeoGlobalSettings {
  id: string;
  site_name: string;
  default_title_pattern: string;
  default_meta_description: string | null;
  default_og_image_url: string | null;
  default_twitter_card: string;
  person_name: string | null;
  person_headline: string | null;
  person_same_as: string[] | null;
  website_url: string | null;
  updated_at: string;
}

export interface SeoPageTypeDefault {
  id: string;
  page_type: string;
  title_pattern: string | null;
  description_pattern: string | null;
  og_image_strategy: string;
  index_default: boolean;
  follow_default: boolean;
  updated_at: string;
}

export interface SeoOverride {
  id: string;
  entity_type: string;
  entity_id: string | null;
  slug: string | null;
  seo_title: string | null;
  seo_description: string | null;
  canonical_url: string | null;
  index: boolean | null;
  follow: boolean | null;
  og_title: string | null;
  og_description: string | null;
  og_image_url: string | null;
  twitter_title: string | null;
  twitter_description: string | null;
  twitter_image_url: string | null;
  structured_data_type: string | null;
  updated_at: string;
}

interface EntityData {
  name?: string;
  title?: string;
  project_name?: string;
  description?: string;
  summary?: string;
  excerpt?: string;
  image_url?: string;
  headline?: string;
}

export interface ProfileData {
  full_name: string | null;
  summary: string | null;
  website_url: string | null;
  profile_photo_url: string | null;
  linkedin_url: string | null;
  github_url: string | null;
  twitter_url: string | null;
  instagram_url: string | null;
  youtube_url: string | null;
  facebook_url: string | null;
  tiktok_url: string | null;
  medium_url: string | null;
  behance_url: string | null;
  dribbble_url: string | null;
}

// Helper to extract social links from profile
export function extractSocialLinks(profile: ProfileData | null): string[] {
  if (!profile) return [];
  const links: string[] = [];
  const socialFields: (keyof ProfileData)[] = [
    'linkedin_url', 'github_url', 'twitter_url', 'instagram_url',
    'youtube_url', 'facebook_url', 'tiktok_url', 'medium_url',
    'behance_url', 'dribbble_url'
  ];
  socialFields.forEach(field => {
    const value = profile[field];
    if (value && typeof value === 'string') links.push(value);
  });
  return links;
}

// Extract headline from summary (first sentence)
export function extractHeadline(summary: string | null | undefined): string {
  if (!summary) return '';
  const firstSentence = summary.split(/[.!?]/)[0];
  return firstSentence.length > 100 ? firstSentence.substring(0, 100) + '...' : firstSentence;
}

// Template pattern replacer
function replacePatterns(pattern: string, variables: Record<string, string | undefined>): string {
  let result = pattern;
  for (const [key, value] of Object.entries(variables)) {
    result = result.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value || '');
  }
  return result;
}

export async function fetchSeoSettings(): Promise<{
  global: SeoGlobalSettings | null;
  pageTypes: SeoPageTypeDefault[];
  overrides: SeoOverride[];
  profile: ProfileData | null;
}> {
  const [globalResult, pageTypesResult, overridesResult, profileResult] = await Promise.all([
    supabase.from('seo_global_settings').select('*').maybeSingle(),
    supabase.from('seo_page_type_defaults').select('*'),
    supabase.from('seo_overrides').select('*'),
    supabase.from('profile').select('full_name, summary, website_url, profile_photo_url, linkedin_url, github_url, twitter_url, instagram_url, youtube_url, facebook_url, tiktok_url, medium_url, behance_url, dribbble_url').maybeSingle(),
  ]);

  return {
    global: globalResult.data as SeoGlobalSettings | null,
    pageTypes: (pageTypesResult.data || []) as SeoPageTypeDefault[],
    overrides: (overridesResult.data || []) as SeoOverride[],
    profile: profileResult.data as ProfileData | null,
  };
}

export async function resolveSeoForRoute(
  pageType: string,
  entity?: EntityData & { id?: string; slug?: string },
  baseUrl?: string
): Promise<SeoConfig> {
  const { global, pageTypes, overrides, profile } = await fetchSeoSettings();
  
  // If this is a project, fetch the cover image
  let projectCoverImage: string | undefined;
  if (pageType === 'project' && entity?.id) {
    const { data: images } = await supabase
      .from('project_images')
      .select('image_url')
      .eq('project_id', entity.id)
      .order('display_order')
      .limit(1);
    if (images && images.length > 0) {
      projectCoverImage = images[0].image_url;
    }
  }
  
  // Merge profile data with global settings (profile is source of truth, global can override)
  const personName = global?.person_name || profile?.full_name || '';
  const personHeadline = global?.person_headline || extractHeadline(profile?.summary);
  const websiteUrl = global?.website_url || profile?.website_url || '';
  const personSameAs = (global?.person_same_as && global.person_same_as.length > 0) 
    ? global.person_same_as 
    : extractSocialLinks(profile);
  const defaultOgImage = global?.default_og_image_url || profile?.profile_photo_url || undefined;
  
  const siteName = global?.site_name || 'Portfolio';
  const currentUrl = baseUrl || (typeof window !== 'undefined' ? window.location.href : '');

  // Find page type defaults
  const pageTypeDefaults = pageTypes.find(pt => pt.page_type === pageType);
  
  // Find override by entity id or slug
  const override = overrides.find(o => 
    (entity?.id && o.entity_id === entity.id) ||
    (entity?.slug && o.slug === entity.slug) ||
    (o.entity_type === pageType && !o.entity_id && !o.slug)
  );

  // Build template variables
  const templateVars: Record<string, string | undefined> = {
    siteName,
    pageTitle: entity?.title || entity?.name || entity?.project_name || pageType,
    projectName: entity?.project_name,
    name: entity?.name || personName,
    headline: entity?.headline || personHeadline,
    title: entity?.title,
    description: entity?.description,
    summary: entity?.summary,
    excerpt: entity?.excerpt,
  };

  // Resolve title with inheritance: override -> page type -> global
  const title = override?.seo_title 
    || (pageTypeDefaults?.title_pattern ? replacePatterns(pageTypeDefaults.title_pattern, templateVars) : null)
    || (global?.default_title_pattern ? replacePatterns(global.default_title_pattern, templateVars) : siteName);
  
  // Resolve description
  const description = override?.seo_description
    || (pageTypeDefaults?.description_pattern ? replacePatterns(pageTypeDefaults.description_pattern, templateVars) : null)
    || global?.default_meta_description
    || entity?.description
    || entity?.summary
    || '';

  // Resolve OG image with inheritance: override -> project cover -> entity image -> global
  let ogImage = override?.og_image_url;
  if (!ogImage) {
    const strategy = pageTypeDefaults?.og_image_strategy || 'use_global_default';
    if (strategy === 'use_item_image') {
      // For projects, prefer cover image from project_images, fallback to image_url
      ogImage = projectCoverImage || entity?.image_url;
    }
    if (!ogImage) {
      ogImage = defaultOgImage;
    }
  }

  // Resolve robots
  const index = override?.index ?? pageTypeDefaults?.index_default ?? true;
  const follow = override?.follow ?? pageTypeDefaults?.follow_default ?? true;

  // Build JSON-LD
  const jsonLd: Record<string, unknown>[] = [];

  // Website schema
  if (websiteUrl) {
    jsonLd.push({
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": siteName,
      "url": websiteUrl,
    });
  }

  // Person schema for resume page (auto-populated from profile)
  if (pageType === 'resume' && personName) {
    const personSchema: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": "Person",
      "name": personName,
    };
    if (personHeadline) personSchema.jobTitle = personHeadline;
    if (websiteUrl) personSchema.url = websiteUrl;
    if (personSameAs.length > 0) {
      personSchema.sameAs = personSameAs;
    }
    if (defaultOgImage) personSchema.image = defaultOgImage;
    jsonLd.push(personSchema);
  }

  // Entity-specific schema
  const structuredDataType = override?.structured_data_type;
  if (structuredDataType && structuredDataType !== 'None' && entity) {
    jsonLd.push({
      "@context": "https://schema.org",
      "@type": structuredDataType,
      "name": entity.title || entity.project_name || entity.name,
      "description": description,
      ...(entity.image_url && { "image": entity.image_url }),
    });
  }

  return {
    title,
    description,
    canonicalUrl: override?.canonical_url || currentUrl,
    robots: { index, follow },
    openGraph: {
      title: override?.og_title || title,
      description: override?.og_description || description,
      image: ogImage,
      url: currentUrl,
      type: pageType === 'writing' ? 'article' : 'website',
      siteName,
    },
    twitter: {
      card: global?.default_twitter_card || 'summary_large_image',
      title: override?.twitter_title || override?.og_title || title,
      description: override?.twitter_description || override?.og_description || description,
      image: override?.twitter_image_url || ogImage,
    },
    jsonLd,
  };
}

// SEO health check utilities
export interface SeoHealthIssue {
  type: 'error' | 'warning' | 'info';
  message: string;
  field: string;
  entityType?: string;
  entityId?: string;
  slug?: string;
}

export function checkTitleLength(title: string): SeoHealthIssue | null {
  if (!title) {
    return { type: 'error', message: 'Title is missing', field: 'title' };
  }
  if (title.length < 30) {
    return { type: 'warning', message: 'Title is too short (less than 30 characters)', field: 'title' };
  }
  if (title.length > 60) {
    return { type: 'warning', message: 'Title is too long (more than 60 characters)', field: 'title' };
  }
  return null;
}

export function checkDescriptionLength(description: string): SeoHealthIssue | null {
  if (!description) {
    return { type: 'error', message: 'Description is missing', field: 'description' };
  }
  if (description.length < 50) {
    return { type: 'warning', message: 'Description is too short (less than 50 characters)', field: 'description' };
  }
  if (description.length > 160) {
    return { type: 'warning', message: 'Description is too long (more than 160 characters)', field: 'description' };
  }
  return null;
}

export function checkOgImage(imageUrl: string | undefined): SeoHealthIssue | null {
  if (!imageUrl) {
    return { type: 'warning', message: 'OG image is missing', field: 'og_image' };
  }
  return null;
}
